import React, { useState } from 'react'
import { useQuery } from 'react-query';
import { BaseHttpService } from 'cms/core/services';
import { Utils } from 'core/utils';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import TranslationForm from '../components/TranslationForm';
import AdminLayout from 'cms/core/shared/AdminLayout';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

function TranslationsPage() {
  const [translations, setData] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [query, setQuery] = useState("");

  const { isLoading, error, data, refetch } = useQuery(['translations', query], () => { return BaseHttpService.searchTranslations(query) }, {
    onSuccess: (data) => {
      setData(data.results)
    },
    refetchInterval: 60 * 10000,
  });

  const [selectedId, setSelectedId] = useState(0);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);

  const _handleEdit = (e, id) => {
    e.preventDefault();
    setSelectedId(id);
    setRefresh(refresh + 1)
    setEditModalIsOpen(true)
  }

  const _handleDelete = (e, id) => {
    e.preventDefault();
    setSelectedId(id);
    setRefresh(refresh + 1)
    setDeleteDialogIsOpen(true)
  }

  const _onDeleteDone = () => {
    var temp = translations.filter(item => item.id !== selectedId)
    setData(temp)
    setDeleteDialogIsOpen(false);
    setRefresh(refresh + 1)
  }

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    // Debounce search - you can add debounce logic here if needed
  }

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    refetch();
  }

  const handleClearSearch = () => {
    setQuery("");
    refetch();
  }

  return (
    <AdminLayout>
      <Box>
        {/* Search and Add Button Section */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          sx={{ mb: 3 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box component="form" onSubmit={handleSearchSubmit} sx={{ flexGrow: 1, maxWidth: { sm: '400px' } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search translations..."
              value={query}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: query && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setSelectedId(null); setEditModalIsOpen(true) }}
            sx={{
              minWidth: { xs: '100%', sm: 'auto' },
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              }
            }}
          >
            Add Translation
          </Button>
        </Stack>

        {/* Results Count */}
        {translations && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translations.length} {translations.length === 1 ? 'translation' : 'translations'} found
          </Typography>
        )}

        {/* Loading State */}
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load translations. Please try again.
          </Alert>
        )}

        {/* Translations Table */}
        {!isLoading && translations && (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, width: '80px' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Default Value</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: '120px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {translations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No translations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  translations.map((item) => (
                    <TableRow 
                      key={"trans" + item.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                          cursor: 'pointer'
                        }
                      }}
                    >
                      <TableCell>
                        <Chip 
                          label={item.id} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          className="kfont"
                          onClick={(e) => { _handleEdit(e, item.id) }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'primary.main',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {item.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="kfont" color="text.secondary">
                          {Utils.getDefaultValue(item, 2) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton color="primary"
                            size="small"
                            onClick={(e) => { _handleEdit(e, item.id) }}
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { _handleDelete(e, item.id) }}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                bgcolor: 'error.light',
                                color: 'error.contrastText'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <CustomModal big key={editModalIsOpen} setOpen={(value) => setEditModalIsOpen(value)}
        open={editModalIsOpen} >
        <TranslationForm id={selectedId} onEditDone={() => { refetch(); setEditModalIsOpen(false) }} />
      </CustomModal>

      <DeleteDialog
        url={`/api/translate/${selectedId}`}
        isOpen={deleteDialogIsOpen}
        key={"mdl" + deleteDialogIsOpen + selectedId}
        setOpen={setDeleteDialogIsOpen}
        notifyDone={_onDeleteDone}
      />
    </AdminLayout>
  )
}

export default TranslationsPage